import React, { useEffect, useRef, useState } from "react";
import { C } from "../constants";

/* ─── SCROLL REVEAL WRAPPER ──────────────────────────────────────────────── */
export function Reveal({ children, delay = 0, from = "bottom", style = {} }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const transforms = {
    bottom: "translateY(32px)",
    top:    "translateY(-32px)",
    left:   "translateX(-32px)",
    right:  "translateX(32px)",
  };

  return (
    <div
      ref={ref}
      style={{
        opacity:    visible ? 1 : 0,
        transform:  visible ? "none" : transforms[from],
        transition: `opacity 0.72s ease ${delay}ms, transform 0.72s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ─── EYEBROW LABEL ─────────────────────────────────────────────────────── */
export function Eyebrow({ text, center = false }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: center ? "center" : "flex-start", marginBottom: 14 }}>
      <span style={{ width: 28, height: 2, background: C.accent, flexShrink: 0 }} />
      <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 11, letterSpacing: 4, textTransform: "uppercase", color: C.accent }}>
        {text}
      </span>
    </div>
  );
}

/* ─── DISPLAY HEADING ───────────────────────────────────────────────────── */
export function H({ children, size = "xl", color = C.black, style = {} }) {
  const sizes = {
    xl: "clamp(38px, 6vw, 76px)",
    lg: "clamp(28px, 4vw, 52px)",
    md: "clamp(22px, 3vw, 36px)",
    sm: "clamp(16px, 2vw, 22px)",
  };
  return (
    <h2 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: sizes[size], lineHeight: 1.06, color, letterSpacing: "-0.025em", ...style }}>
      {children}
    </h2>
  );
}

/* ─── BODY TEXT ─────────────────────────────────────────────────────────── */
export function P({ children, size = "md", color = C.gray, style = {} }) {
  const sizes = { lg: 19, md: 16, sm: 14 };
  return (
    <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: sizes[size], color, lineHeight: 1.85, fontWeight: 400, ...style }}>
      {children}
    </p>
  );
}

/* ─── BUTTON ────────────────────────────────────────────────────────────── */
export function Btn({ children, variant = "solid", onClick, style = {} }) {
  const [hov, setHov] = useState(false);
  const base = {
    fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 12,
    letterSpacing: 2, textTransform: "uppercase", padding: "13px 32px",
    border: "2px solid", cursor: "pointer",
    transition: "all 0.25s ease",
    display: "inline-flex", alignItems: "center", gap: 8,
    ...style,
  };

  if (variant === "solid") return (
    <button
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      onClick={onClick}
      style={{ ...base, background: hov ? C.accentDark : C.accent, borderColor: hov ? C.accentDark : C.accent, color: C.white }}
    >{children}</button>
  );

  if (variant === "outline") return (
    <button
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      onClick={onClick}
      style={{ ...base, background: hov ? C.accent : "transparent", borderColor: C.accent, color: hov ? C.white : C.accent }}
    >{children}</button>
  );

  if (variant === "ghost") return (
    <button
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      onClick={onClick}
      style={{ ...base, border: "none", background: "none", padding: "0", color: hov ? C.accent : C.black, fontSize: 13 }}
    >
      {children}
      <span style={{ display: "inline-block", transform: hov ? "translateX(5px)" : "none", transition: "transform 0.22s" }}>→</span>
    </button>
  );
}

/* ─── CHIP / TAG ────────────────────────────────────────────────────────── */
export function Chip({ children }) {
  return (
    <span style={{
      display: "inline-block",
      fontFamily: "'Poppins', sans-serif", fontSize: 11, fontWeight: 600,
      color: C.accent, background: C.accentLight,
      padding: "4px 13px", borderRadius: 100, letterSpacing: 0.5,
    }}>
      {children}
    </span>
  );
}

/* ─── SECTION WRAPPER ───────────────────────────────────────────────────── */
export function Sec({ children, bg = C.white, style = {} }) {
  return (
    <section style={{ background: bg, padding: "96px clamp(20px, 7vw, 120px)", ...style }}>
      {children}
    </section>
  );
}
